import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  Renderer2
} from '@angular/core';
import { Router } from '@angular/router';
import { BrandingService } from '@approot/branding';
import {
  MarketDataService,
  Message
} from '@approot/monitoring/market-data/market-data.service';
import {
  AdminService,
  AuthService,
  ComponentVisibilityService,
  ConfigService,
  MarketsService,
  TradesCaptureService,
  UserSession
} from '@approot/services';
import { parseJWT } from '@approot/utilities';
import { DisplayDefinitionsService } from '@approot/widgets';
import buildNumber from '@root/build_number';
import { Once, Throttle } from 'lodash-decorators';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

type WarningModalTypes =
  | 'admin'
  | 'userManagement'
  | 'manageStaticData'
  | 'whiteLabelTradingRules';

type WarningModalSettings = {
  showModal: boolean;
  modal: WarningModalTypes;
};

@Component({
  selector: 'app-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainMenuComponent {
  public logoToShow = { name: '', src: '' };
  public warningModalSettings: WarningModalSettings = {
    showModal: false,
    modal: null
  };
  public isMarketDataOpen = false;
  public showUserManagement = false;
  public showWhiteLabelTradingRules = false;
  public showManageStaticData: boolean;
  public showAdmin = false;
  public showPerseusAdmin = false;
  public showMonitor = false;
  public csaEnabled: boolean;
  public creditCurvesEnabled: boolean;
  public scheduleReportsEnabled: boolean;
  public alertsEnabled: boolean;
  public buildNumber = buildNumber;
  public isDciGridAccessAuthorized = false;
  public isVolSurfaceReadAccessAuthorized = false;
  public isForwardCurveReadAccessAuthorized = false;
  public isEntitiesReadAccessAuthorized: boolean;
  public isTreasuryCurvesReadAccessAuthorized: boolean;
  public isFixingsReadAccessAuthorized: boolean;
  public isDefinitionsReadAccessAuthorized: boolean;
  public showUserManualLink = false;
  public showCreditLines = false;
  public showMarketDataUpdates = false;
  public latestBuildNo = '';
  public showPricingEnviromentList: boolean;
  public showToolsList: boolean;
  public showMarketDataList: boolean;

  constructor(
    public brand: BrandingService,
    private componentVisibilityService: ComponentVisibilityService,
    private marketsService: MarketsService,
    private renderer: Renderer2,
    private elem: ElementRef,
    private tradesCaptureService: TradesCaptureService,
    private adminService: AdminService,
    private router: Router,
    private displayDefinitionsService: DisplayDefinitionsService,
    private configService: ConfigService,
    private authService: AuthService,
    private httpClient: HttpClient,
    private marketDataService: MarketDataService,
    private authSvc: AuthService,
    private changeRef: ChangeDetectorRef
  ) {
    effect(async () => {
      if (this.showMarketDataUpdates) {
        return;
      }
      let messages = this.marketDataService.messages();
      if (messages.length > 0) {
        await this.checkForMarketDataUpdates(messages);
      }
      this.changeRef.markForCheck();
    });

    effect(async () => {
      const config = this.configService.userSettings();
      this.showUserManagement = config.isUserManagementAccessAuthorized;
      this.csaEnabled = !!config.parent.csaDataBaseRootName;
      this.creditCurvesEnabled = !!config.parent.creditCurvesDataBaseRootName;
      this.scheduleReportsEnabled = config.parent.enableReports;
      this.alertsEnabled = !!config.parent.enableAlerts;
      this.showManageStaticData = config.isStaticDataManagementAccessAuthorized;
      this.isDefinitionsReadAccessAuthorized =
        config.isDefinitionsReadAccessAuthorized;
      this.showWhiteLabelTradingRules = config.enableWhiteLabelTradingRules;
      this.isDciGridAccessAuthorized = config.isDciGridAccessAuthorized;
      this.isVolSurfaceReadAccessAuthorized =
        config.isVolSurfaceReadAccessAuthorized;
      this.isForwardCurveReadAccessAuthorized =
        config.isForwardCurveReadAccessAuthorized;
      this.isEntitiesReadAccessAuthorized =
        config.isEntitiesReadAccessAuthorized;
      this.isTreasuryCurvesReadAccessAuthorized =
        config.isTreasuryCurvesReadAccessAuthorized;
      this.isFixingsReadAccessAuthorized = config.isFixingsReadAccessAuthorized;
      this.showPerseusAdmin = config.isPerseusAdmin;
      this.showCreditLines = config.parent.enableCreditLines;
      this.showAdmin = await this.shouldShowAdmin();
      this.showMonitor = this.showAdmin;
      this.changeRef.markForCheck();

      // Wait a jot for change detection
      setTimeout(() => this.checkShowMenuItems(), 50);
      // Double check
      setTimeout(() => this.checkShowMenuItems(), 500);
    });

    const params = { task: 'getUserManualPresignedUrl' };
    this.authService
      .fxPost<any>(params, `get-user-info`, params)
      .subscribe(r => (this.showUserManualLink = !!r.url));

    setTimeout(async () => {
      this.latestBuildNo = await this.getLatestBuildNumber();
    }, 0);
    setInterval(async () => {
      this.latestBuildNo = await this.getLatestBuildNumber();
    }, 300_000);
  }

  @Throttle(10_000)
  private async checkForMarketDataUpdates(messages: Message[]) {
    try {
      // Once set to true, this will prevent further checks
      if (this.showMarketDataUpdates) {
        return;
      }
      if (!messages || messages.length === 0) {
        return;
      }
      const cognito = await this.authSvc.currentSession();
      const jwt = parseJWT(cognito.getIdToken().getJwtToken());
      const admin = jwt['custom:isAdmin'] === 'true';
      if (!admin) {
        return;
      }
      const group = jwt['cognito:groups']?.[0];

      messages = messages.filter(
        m => m.name?.toLocaleUpperCase() === group?.toLocaleUpperCase()
      );
      if (messages.length > 0) {
        this.showMarketDataUpdates = true;
      }
    } finally {
      this.checkShowMenuItems();
    }
  }

  public get mainLogo() {
    return this.adminService.getImageUrl('mainLogo');
  }

  public open() {
    const element = this.elem.nativeElement.querySelector(
      '.menu-contents'
    ) as HTMLElement;

    // add the hover class to initially open the menu,
    // then remove it and rely on the :hover selector to keep menu open
    this.renderer.addClass(element, 'hover');
    setTimeout(() => this.renderer.removeClass(element, 'hover'), 1000);
  }

  private checkShowMenuItems() {
    const el = this.elem.nativeElement;
    const hasItems = (selector: string): boolean => {
      const list = el.querySelectorAll(`#${selector} li`);
      return list.length > 0;
    };

    this.showToolsList = hasItems('toolsList');
    this.showPricingEnviromentList = hasItems('pricingEnviromentList');
    this.showMarketDataList = hasItems('marketDataList');
    this.changeRef.markForCheck();
  }

  public setShowFixings(val: boolean) {
    this.marketsService.showFixings = val;
  }

  public openIRFixingSources(val: boolean): void {
    this.marketsService.showIRFixings = val;
  }

  public setShowCreditLines(val: boolean) {
    this.marketsService.showCreditLines = val;
  }

  public setShowEntities(val: boolean) {
    this.tradesCaptureService.setShowEntities = val;
  }

  public setShowCsa(val: boolean) {
    if (this.csaEnabled) {
      this.tradesCaptureService.setShowCsa = val;
    }
  }

  public setShowCreditCurves(val: boolean) {
    if (this.creditCurvesEnabled) {
      this.tradesCaptureService.setShowCreditCurves = val;
    }
  }

  public setShowTreasuryCurves(val: boolean) {
    if (this.isTreasuryCurvesReadAccessAuthorized) {
      this.tradesCaptureService.setShowTreasuryCurves = val;
    }
  }

  public setShowScheduleReports(val: boolean) {
    if (this.scheduleReportsEnabled) {
      this.tradesCaptureService.setShowScheduleReports = val;
    }
  }

  public setShowAlerts(val: boolean) {
    if (this.alertsEnabled) {
      this.tradesCaptureService.setShowAlerts = val;
    }
  }

  public showDciGrid(): void {
    this.marketsService.showDci = this.isDciGridAccessAuthorized;
  }

  public openMarketData(event: any = true) {
    this.isMarketDataOpen = !!event;
  }

  public get dvEntitiesExists() {
    return this.tradesCaptureService.dvEntities;
  }

  public updateStrategiesGridVis(val: boolean) {
    this.componentVisibilityService.updateStrategiesGridVis(val);
  }

  public updatePositionGrid(val: boolean) {
    this.componentVisibilityService.updatePositionGrid(val);
  }

  public updateHistoricalGraphVis() {
    this.componentVisibilityService.updateHistoricalGraphVis();
  }

  public updateForwardVis() {
    this.componentVisibilityService.updateForwardVis();
  }

  public updateVolVis() {
    this.componentVisibilityService.updateVolVis();
  }

  public navigateUrlFromModal() {
    this.warningModalSettings.showModal = false;
    let url = '';
    switch (this.warningModalSettings.modal) {
      case 'userManagement':
        url = '/user-management';
        break;
      case 'manageStaticData':
        url = '/static-data-management';
        break;
      case 'whiteLabelTradingRules':
        url = '/white-label-trading-rules';
        break;
      default:
        return;
    }
    console.log('Navigating to: ', url);
    this.router.navigateByUrl(url);
  }

  public openCurrencyPairs() {
    this.displayDefinitionsService.showCurrencyPairs = true;
  }

  public openFixingSources() {
    this.displayDefinitionsService.showFixingSources = true;
  }

  public openCutoffs() {
    this.displayDefinitionsService.showCutoffs = true;
  }

  public openTradeStatus() {
    this.displayDefinitionsService.showTradeStatus = true;
  }

  public setWarningModalSettings(warningModalType: WarningModalTypes) {
    switch (warningModalType) {
      case 'userManagement':
        this.warningModalSettings.showModal = this.showUserManagement;
        this.warningModalSettings.modal = warningModalType;
        break;
      case 'manageStaticData':
        this.warningModalSettings.showModal = this.showManageStaticData;
        this.warningModalSettings.modal = warningModalType;
        break;
      case 'whiteLabelTradingRules':
        this.warningModalSettings.showModal = this.showWhiteLabelTradingRules;
        this.warningModalSettings.modal = warningModalType;
        break;
      default:
        break;
    }
  }

  public openHydraManual() {
    const params = {
      task: 'getUserManualPresignedUrl'
    };
    this.authService
      .fxPost<any>(params, `get-user-info`, params)
      .subscribe(r => {
        if (r.url != null) {
          // Note: S3 object must have correct MIME type set (application/pdf)
          window.open(r.url, '_blank');
        }
      });
  }

  public async download(url: string, filename: string) {
    this.httpClient.get(url, { responseType: 'blob' }).subscribe(blob => {
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', objectUrl);
      link.setAttribute('download', filename);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  }

  private async getLatestBuildNumber() {
    const response = await fetch('/assets/build_number.json', {
      cache: 'no-store'
    });
    if (response.ok) {
      const buildNumber = await response.json();
      return buildNumber;
    } else {
      return '';
    }
  }

  @Once()
  private async shouldShowAdmin(): Promise<boolean> {
    return await firstValueFrom(
      this.authService.currentSession$().pipe(
        take(1),
        map((session: UserSession) => {
          const token = parseJWT(session.getIdToken().getJwtToken());
          return token['custom:isSuperuser'] === 'true';
        }),
        catchError((err: Error) => {
          console.error(err);
          return of(false);
        })
      )
    );
  }
}
